import { useCallback } from 'react';
import { ConfigType } from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { formatDateTime } from '../helpers';

type FormatDateAndTimeByLocation = (
  locationId: string
) => (dateTime: ConfigType, format: string, shouldShowTimezoneAbbr?: boolean) => string;

export const useLocationTimezone = () => {
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { locationTimezoneMap, userTimezone } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore(
    'locationTimezoneMap',
    'userTimezone'
  );

  // @TODO: Move this to a common place so that both waitlist and schedule can use it
  const formatDateAndTimeByLocation: FormatDateAndTimeByLocation = useCallback(
    (locationId) =>
      (dateTime, format, shouldShowTimezoneAbbr = false) => {
        const {
          timezone: locationTimezone,
          timezoneAbbr,
          isUserOutsideTimezone,
        } = locationTimezoneMap[locationId] || {};

        const formattedDateTime = formatDateTime(dateTime, {
          fromTimezone: 'UTC',
          toTimezone: isScheduleV3FlagEnabled ? locationTimezone : userTimezone,
          outputFormat: format,
        });

        // Show timezone abbreviation only if the user is outside the location's timezone and v3 is enabled
        return isScheduleV3FlagEnabled && isUserOutsideTimezone && shouldShowTimezoneAbbr
          ? `${formattedDateTime} ${timezoneAbbr}`.trim()
          : formattedDateTime;
      },
    [isScheduleV3FlagEnabled, locationTimezoneMap, userTimezone]
  );

  const getLocationTimezone = (locationId: string) => {
    const locationTimezone = locationTimezoneMap?.[locationId];
    return (isScheduleV3FlagEnabled && locationTimezone?.isUserOutsideTimezone && locationTimezone?.timezone) || '';
  };

  const getLocationTimezoneAbbr = (locationId: string) => {
    const locationTimezone = locationTimezoneMap?.[locationId];
    return (isScheduleV3FlagEnabled && locationTimezone?.isUserOutsideTimezone && locationTimezone?.timezoneAbbr) || '';
  };

  return { formatDateAndTimeByLocation, getLocationTimezone, getLocationTimezoneAbbr };
};
